import { getSafeTracer, SpanStatusCode } from '../tracing/setup';
const TRACER_NAME = 'quartz-routing';
const SILO_ACCESSOR = Symbol.for('silo_v1');

// Minimal history interface we depend on — matches quartz-routing's History type.

function getAppName() {
  var _ref, _silo$BROWSING_CONTEX;
  const silo = window[SILO_ACCESSOR];
  return (_ref = silo === null || silo === void 0 || (_silo$BROWSING_CONTEX = silo.BROWSING_CONTEXT) === null || _silo$BROWSING_CONTEX === void 0 || (_silo$BROWSING_CONTEX = _silo$BROWSING_CONTEX.values) === null || _silo$BROWSING_CONTEX === void 0 ? void 0 : _silo$BROWSING_CONTEX.get('current_package_name')) !== null && _ref !== void 0 ? _ref : '';
}
function emitNavSpan(action, fromPath, historyKey, appName) {
  const tracer = getSafeTracer(TRACER_NAME);
  const span = tracer.startSpan(`navigation.${action}`, {
    attributes: {
      'navigation.type': action,
      'navigation.from_path': fromPath,
      'url.path': window.location.pathname,
      'url.query': window.location.search,
      'url.fragment': window.location.hash,
      'navigation.history_key': historyKey,
      'quartz.app_name': appName
    }
  });
  span.setStatus({
    code: SpanStatusCode.OK
  });
  span.end();
}

/**
 * Instruments SPA navigation by patching window.history.pushState /
 * replaceState and listening to popstate. No quartz app plugin needed —
 * call once after initTracing() (or pass instrumentSpaNavigation: true to
 * initTracing) and spans are emitted automatically for the page lifetime.
 * Returns an unlisten function for cleanup.
 */
export function instrumentSpaNavigation() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (window.history.pushState.__otel_spa_nav_patched) {
    return () => {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const origPushState = window.history.pushState.bind(window.history);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const origReplaceState = window.history.replaceState.bind(window.history);
  let previousPath = window.location.pathname;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.history.pushState = function (state, title, url) {
    var _state$key;
    origPushState(state, title, url);
    const appName = getAppName();
    const key = String((_state$key = state === null || state === void 0 ? void 0 : state.key) !== null && _state$key !== void 0 ? _state$key : '');
    emitNavSpan('push', previousPath, key, appName);
    previousPath = window.location.pathname;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.history.pushState.__otel_spa_nav_patched = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.history.replaceState = function (state, title, url) {
    var _state$key2;
    origReplaceState(state, title, url);
    const appName = getAppName();
    const key = String((_state$key2 = state === null || state === void 0 ? void 0 : state.key) !== null && _state$key2 !== void 0 ? _state$key2 : '');
    emitNavSpan('replace', previousPath, key, appName);
    previousPath = window.location.pathname;
  };
  function onPopState(event) {
    var _key, _event$state;
    const appName = getAppName();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const key = String((_key = (_event$state = event.state) === null || _event$state === void 0 ? void 0 : _event$state.key) !== null && _key !== void 0 ? _key : '');
    emitNavSpan('pop', previousPath, key, appName);
    previousPath = window.location.pathname;
  }
  window.addEventListener('popstate', onPopState);
  return () => {
    window.history.pushState = origPushState;
    window.history.replaceState = origReplaceState;
    window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete window.history.pushState.__otel_spa_nav_patched;
  };
}

/**
 * Instruments quartz-routing navigation by attaching a history.listen() observer
 * that emits an instantaneous OTel span per SPA navigation.
 *
 * Call this from onAfterRender in a quartz app plugin, passing the history
 * instance from get(HISTORY_INSTANCE). Returns the unlisten function — store it
 * and call it in onUnmount to prevent duplicate listeners on hot reload.
 */
export function instrumentQuartzRouting(history) {
  let previousPath = history.location.pathname;
  return history.listen((location, action) => {
    var _location$key;
    const from = previousPath;
    previousPath = location.pathname;
    const appName = getAppName();
    const tracer = getSafeTracer(TRACER_NAME);
    const span = tracer.startSpan(`navigation.${action.toLowerCase()}`, {
      attributes: {
        'navigation.type': action.toLowerCase(),
        'navigation.from_path': from,
        'url.path': location.pathname,
        'url.query': location.search,
        'url.fragment': location.hash,
        'navigation.history_key': (_location$key = location.key) !== null && _location$key !== void 0 ? _location$key : '',
        'quartz.app_name': appName
      }
    });
    span.setStatus({
      code: SpanStatusCode.OK
    });
    span.end();
  });
}